import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type CSSProperties,
  type DragEvent,
  type KeyboardEvent,
  type ComponentProps,
  type MouseEvent,
  type PointerEvent,
  type UIEvent,
} from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowUp,
  ChevronRight,
  Maximize2,
  Mic,
  Minimize2,
  PanelRightClose,
  Paperclip,
  Plus,
  Square,
} from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { inAppHref } from './inAppHref'
import remarkGfm from 'remark-gfm'

import { apiFetch } from '../app/auth/apiClient'

import {
  ATTACHMENT_ACCEPT,
  AttachmentCards,
  MAX_CHAT_ATTACHMENT_TOTAL,
  addAttachmentFiles,
  filesAsCards,
} from '../app/components/attachments/AttachmentCards'
import { AuthedImage } from '../app/components/attachments/AuthedImage'
import { AttachmentLink, attachmentUrlTransform, isAttachmentLink } from '../app/components/attachments/AttachmentLink'
import { LumaIcon } from '../app/components/progetto_arca/LumaIcon'
import { ActivityTimelineDrawer, activityTimelineCopy } from './ActivityTimelineDrawer'
import { ChatErrorNotice } from './ChatErrorNotice'
import { ChatLauncherPriorities } from './ChatLauncherPriorities'
import { InputRequestCard } from './InputRequestCard'
import { assistantSegments, createPrewarmGate } from '@agent-runtime/client'
import { useCodexChat } from './useCodexChat'
import { useVoiceInput } from './useVoiceInput'
import styles from './CodexChatRail.module.scss'

const CHAT_IMAGE_SRC = /^\/api\/chat\/[a-z0-9][a-z0-9-]{0,39}\/attachments\/[0-9a-f]{32}$/

function AssistantImage({ src, alt }: ComponentProps<'img'>) {
  if (!src || !CHAT_IMAGE_SRC.test(src)) return <span>{alt || 'Immagine non disponibile'}</span>
  return <AuthedImage src={src} alt={alt || ''} />
}

// In-app paths navigate through the router so the open rail survives the jump.
function AssistantLink({ href, children, conversationId }: ComponentProps<'a'> & { conversationId: string }) {
  if (isAttachmentLink(href)) return <AttachmentLink href={href} conversationId={conversationId}>{children}</AttachmentLink>
  const path = inAppHref(href)
  if (path) {
    return <Link to={path}>{children}</Link>
  }
  return (
    <a href={href} target="_blank" rel="noreferrer">
      {children}
    </a>
  )
}

const MAX_COMPOSER_HEIGHT = 152
const LAUNCHER_EDGE_GAP = 8
const DRAG_THRESHOLD = 4

function placeLauncher(element: HTMLButtonElement, x: number, y: number): void {
  const rect = element.getBoundingClientRect()
  const maxX = Math.max(LAUNCHER_EDGE_GAP, window.innerWidth - rect.width - LAUNCHER_EDGE_GAP)
  const maxY = Math.max(LAUNCHER_EDGE_GAP, window.innerHeight - rect.height - LAUNCHER_EDGE_GAP)
  element.style.left = `${Math.min(Math.max(LAUNCHER_EDGE_GAP, x), maxX)}px`
  element.style.top = `${Math.min(Math.max(LAUNCHER_EDGE_GAP, y), maxY)}px`
  element.style.right = 'auto'
}

function useDraggableLauncher(onOpen: () => void) {
  const dragRef = useRef<{
    pointerId: number
    startX: number
    startY: number
    originX: number
    originY: number
    moved: boolean
  } | null>(null)
  const suppressClickRef = useRef(false)

  const onPointerDown = useCallback((event: PointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0 || event.isPrimary === false) return
    const rect = event.currentTarget.getBoundingClientRect()
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: rect.left,
      originY: rect.top,
      moved: false,
    }
    suppressClickRef.current = false
    event.currentTarget.dataset.dragging = 'true'
    event.currentTarget.setPointerCapture?.(event.pointerId)
  }, [])

  const onPointerMove = useCallback((event: PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== event.pointerId) return
    const deltaX = event.clientX - drag.startX
    const deltaY = event.clientY - drag.startY
    if (!drag.moved && Math.hypot(deltaX, deltaY) < DRAG_THRESHOLD) return
    drag.moved = true
    suppressClickRef.current = true
    placeLauncher(event.currentTarget, drag.originX + deltaX, drag.originY + deltaY)
    event.preventDefault()
  }, [])

  const finishDrag = useCallback((event: PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== event.pointerId) return
    delete event.currentTarget.dataset.dragging
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    dragRef.current = null
  }, [])

  const onClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (suppressClickRef.current) {
        suppressClickRef.current = false
        event.preventDefault()
        return
      }
      onOpen()
    },
    [onOpen],
  )

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp: finishDrag,
    onPointerCancel: finishDrag,
    onClick,
  }
}

// The runner keeps one warm Codex container per conversation that expires
// after 15 idle minutes; pinging on typing (not navigation) hides the cold
// boot. Without a conversation the runtime defers to the first message.
const prewarmGate = createPrewarmGate()
function prewarmWorker(conversationId?: string) {
  if (!prewarmGate(conversationId ?? '', Date.now())) return
  void apiFetch('/api/chat/prewarm', {
    method: 'POST',
    ...(conversationId
      ? {
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ conversation_id: conversationId }),
        }
      : {}),
  }).catch(() => {})
}

// The composer stays open while an answer streams: a message sent then
// steers the running turn. `disabled` is for hosts that have nothing to
// send to yet (no insight loaded), never for a turn in progress.
export function ChatComposer({
  disabled = false,
  onSend,
  files,
  attachmentError,
  onFilesChange,
  onAddFiles,
  prefill = '',
  conversationId,
}: {
  disabled?: boolean
  onSend: (prompt: string, files?: File[]) => void
  files: File[]
  attachmentError: string
  onFilesChange: (files: File[]) => void
  onAddFiles: (files: File[]) => void
  prefill?: string
  conversationId?: string
}) {
  const [value, setValue] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  useEffect(() => {
    if (prefill === '') return
    setValue(prefill)
    textareaRef.current?.focus()
  }, [prefill])
  const inputRef = useRef<HTMLInputElement | null>(null)
  const voiceBaseRef = useRef('')
  const voice = useVoiceInput((committed, partial) => {
    setValue(
      [voiceBaseRef.current.trim(), committed, partial]
        .filter((part) => part !== '')
        .join(' '),
    )
  })
  const trimmed = value.trim()

  useLayoutEffect(() => {
    const textarea = textareaRef.current
    if (!textarea) return
    textarea.style.height = 'auto'
    textarea.style.height = `${Math.min(textarea.scrollHeight, MAX_COMPOSER_HEIGHT)}px`
  }, [value])

  const submit = useCallback(() => {
    if (disabled || (trimmed === '' && files.length === 0)) return
    voice.stop()
    onSend(value, files)
    setValue('')
    onFilesChange([])
  }, [disabled, files, onFilesChange, onSend, trimmed, value, voice])

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key !== 'Enter' || event.shiftKey) return
    event.preventDefault()
    submit()
  }

  function handleChange(event: ChangeEvent<HTMLTextAreaElement>) {
    setValue(event.target.value)
    if (event.target.value.trim() !== '') prewarmWorker(conversationId)
  }

  return (
    <div className={styles.composerShell}>
      <AttachmentCards
        items={filesAsCards(files)}
        onRemove={(key) => onFilesChange(files.filter((file) =>
          `${file.name}:${file.size}:${file.lastModified}` !== key))}
      />
      <div className={styles.composerRow}>
        <input
          ref={inputRef}
          className={styles.fileInput}
          type="file"
          multiple
          accept={ATTACHMENT_ACCEPT}
          tabIndex={-1}
          onChange={(event) => {
            onAddFiles(Array.from(event.target.files ?? [])); event.target.value = ''
          }}
        />
        <button
          type="button"
          className={styles.attachButton}
          aria-label="Allega file"
          title="Allega immagini, PDF, Excel e altri file"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
        >
          <Paperclip size={18} aria-hidden />
        </button>
        <textarea
          ref={textareaRef}
          className={styles.textarea}
          rows={1}
          value={value}
          placeholder="Chiedi qualcosa sui dati…"
          aria-label="Messaggio"
          onChange={handleChange}
          onPaste={(event) => {
            const pasted = Array.from(event.clipboardData.files)
            if (pasted.length > 0) { event.preventDefault(); onAddFiles(pasted) }
          }}
          onKeyDown={handleKeyDown}
        />
        {voice.supported ? (
          <button
            type="button"
            className={styles.micButton}
            aria-label={voice.recording ? 'Interrompi dettatura' : 'Detta un messaggio'}
            title={voice.recording ? 'Interrompi dettatura' : 'Detta un messaggio'}
            data-recording={voice.recording || undefined}
            style={{ '--voice-level': voice.level } as CSSProperties}
            disabled={disabled}
            onClick={() => {
              if (voice.recording) {
                voice.stop()
                return
              }
              voiceBaseRef.current = value
              prewarmWorker(conversationId)
              void voice.start()
            }}
          >
            <Mic size={18} aria-hidden />
          </button>
        ) : null}
        <button
          type="button"
          className={styles.sendButton}
          aria-label="Invia messaggio"
          disabled={disabled || (trimmed === '' && files.length === 0)}
          onClick={submit}
        >
          <ArrowUp size={18} aria-hidden />
        </button>
      </div>
      {attachmentError ? <span className={styles.attachmentError} role="alert">{attachmentError}</span> : null}
    </div>
  )
}

export function CodexChatRail({
  agentName,
  conversationId,
  collapsed,
  workspace = false,
  showEmptyLauncher = false,
  composerPrefill = '',
  screenContextUrl,
  launcherPulse = 0,
  ready = false,
  hidden = false,
  onNewConversation,
  onOpenWorkspace,
  onMinimizeWorkspace,
  onCollapsedChange,
  onStartedChange,
  onRunningChange,
}: {
  agentName: string
  conversationId: string
  collapsed: boolean
  workspace?: boolean
  showEmptyLauncher?: boolean
  composerPrefill?: string
  screenContextUrl?: string
  launcherPulse?: number
  ready?: boolean
  hidden?: boolean
  onNewConversation: () => void
  onOpenWorkspace?: () => void
  onMinimizeWorkspace: () => void
  onCollapsedChange: (next: boolean) => void
  onStartedChange?: (started: boolean) => void
  onRunningChange?: (running: boolean) => void
}) {
  const {
    messages,
    activities,
    commentary,
    error,
    inputs,
    turnMode,
    running,
    reconnecting,
    send,
    cancel,
    retry,
    respondToInput,
    historyLoaded,
  } = useCodexChat(conversationId, screenContextUrl)
  const conversationRef = useRef<HTMLDivElement | null>(null)
  const pinnedRef = useRef(true)
  const scrollConversationRef = useRef(conversationId)
  const [files, setFiles] = useState<File[]>([])
  const [attachmentError, setAttachmentError] = useState('')
  const [draggingFiles, setDraggingFiles] = useState(false)
  const [activityTurnId, setActivityTurnId] = useState<string | null>(null)
  const started = messages.length > 0 || running
  const showLauncher = historyLoaded && !started && showEmptyLauncher
  const launcherDrag = useDraggableLauncher(() => onCollapsedChange(false))

  useEffect(() => {
    onStartedChange?.(started)
  }, [onStartedChange, started])

  useEffect(() => {
    onRunningChange?.(running)
  }, [onRunningChange, running])

  useEffect(() => {
    setFiles([])
    setAttachmentError('')
    setDraggingFiles(false)
    setActivityTurnId(null)
  }, [conversationId])

  const addFiles = useCallback((next: File[]) => {
    setFiles((current) => {
      const result = addAttachmentFiles(current, next, MAX_CHAT_ATTACHMENT_TOTAL)
      setAttachmentError(result.error)
      return result.files
    })
  }, [])

  const attachmentDropProps = {
    className: styles.dropZone,
    'data-dragging-files': draggingFiles || undefined,
    onDragEnter: (event: DragEvent<HTMLElement>) => {
      if (!event.dataTransfer.types.includes('Files')) return
      event.preventDefault()
      setDraggingFiles(true)
    },
    onDragOver: (event: DragEvent<HTMLElement>) => {
      if (event.dataTransfer.types.includes('Files')) event.preventDefault()
    },
    onDragLeave: (event: DragEvent<HTMLElement>) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node)) setDraggingFiles(false)
    },
    onDrop: (event: DragEvent<HTMLElement>) => {
      event.preventDefault()
      setDraggingFiles(false)
      addFiles(Array.from(event.dataTransfer.files))
    },
  }

  const handleSend = useCallback(
    (prompt: string, files: File[] = []) => {
      pinnedRef.current = true
      onCollapsedChange(false)
      void send(prompt, files)
    },
    [onCollapsedChange, send],
  )

  const handleScroll = useCallback((event: UIEvent<HTMLDivElement>) => {
    const element = event.currentTarget
    pinnedRef.current = element.scrollHeight - element.scrollTop - element.clientHeight <= 80
  }, [])

  const terminalActivitiesByMessage = useMemo(() => {
    const latestAssistantByTurn = new Map<string, string>()
    let turnId: string | undefined
    for (const message of messages) {
      if (message.role === 'user') turnId = message.id
      else if (turnId) latestAssistantByTurn.set(turnId, message.id)
    }

    const grouped = new Map<string, typeof activities>()
    for (const activity of activities) {
      if (activity.status === 'running' || !activity.turnId) continue
      const messageId = latestAssistantByTurn.get(activity.turnId)
      if (!messageId) continue
      const group = grouped.get(messageId)
      if (group) group.push(activity)
      else grouped.set(messageId, [activity])
    }
    return grouped
  }, [activities, messages])
  const hasRunningActivity = activities.some((activity) => activity.status === 'running')
  const latestTurnId = useMemo(() => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      if (messages[index].role === 'user') return messages[index].id
    }
    return null
  }, [messages])
  const liveAssistantMessageId = useMemo(() => {
    if (!running || !latestTurnId) return null
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const message = messages[index]
      if (message.id === latestTurnId) return null
      if (message.role === 'assistant') return message.id
    }
    return null
  }, [latestTurnId, messages, running])
  const promptsByTurnId = useMemo(() => new Map(messages
    .filter((message) => message.role === 'user')
    .map((message) => [message.id, message.text])), [messages])
  const selectedActivities = useMemo(
    () => activities.filter((activity) => activity.turnId === activityTurnId),
    [activities, activityTurnId],
  )
  const selectedActivityPrompt = promptsByTurnId.get(activityTurnId ?? '') ?? ''
  const liveActivityCopy = activityTimelineCopy(promptsByTurnId.get(latestTurnId ?? '') ?? '')
  const liveActivityLabel = reconnecting
    ? 'Riconnessione alla risposta in corso…'
    : turnMode === 'steered'
      ? 'Il messaggio si unisce alla risposta in corso…'
      : hasRunningActivity
        ? liveActivityCopy.working
        : liveActivityCopy.thinking
  const selectedActivityIsRunning = running && activityTurnId === latestTurnId
  const liveActivityControl = running ? (
    <>
      <button
        type="button"
        className={styles.liveActivityTrigger}
        aria-expanded={activityTurnId === latestTurnId}
        onClick={() => setActivityTurnId((current) =>
          current === latestTurnId ? null : latestTurnId)}
      >
        <span className="chat-breathing-dot" />
        {liveActivityLabel}
        <ChevronRight size={13} aria-hidden />
      </button>
      <button
        type="button"
        className={styles.stopTrigger}
        aria-label="Interrompi la risposta"
        title="Interrompi la risposta"
        onClick={() => void cancel()}
      >
        <Square size={11} aria-hidden />
        Interrompi
      </button>
      <span className={styles.srOnly} role="status">{liveActivityLabel}</span>
    </>
  ) : null

  useLayoutEffect(() => {
    if (scrollConversationRef.current !== conversationId) {
      scrollConversationRef.current = conversationId
      pinnedRef.current = true
    }
    const conversation = conversationRef.current
    if (!conversation || !pinnedRef.current) return
    conversation.scrollTop = conversation.scrollHeight
  }, [activities, conversationId, error, messages, running])

  if (hidden) return null
  if (!historyLoaded) return null

  if (showLauncher) {
    return (
      <section
        {...attachmentDropProps}
        className={`agent-chat agent-chat--ace ${styles.launcherSurface} ${attachmentDropProps.className}`}
        aria-label={`Chat con ${agentName}`}
        data-launcher="true"
      >
        {workspace ? (
          <button
            type="button"
            className={styles.launcherMinimize}
            aria-label="Riduci a Luma"
            onClick={onMinimizeWorkspace}
          >
            <Minimize2 size={16} aria-hidden />
            <span>Riduci a Luma</span>
          </button>
        ) : null}
        <div className={styles.launcherScroll}>
          <div className={styles.launcher}>
            <div className={styles.launcherHeading}>
              <p className={styles.launcherEyebrow}>Nuova conversazione con {agentName}</p>
              <h1 className={styles.launcherTitle}>Cosa ti serve o vuoi approfondire?</h1>
            </div>
            <ChatLauncherPriorities onNavigate={() => onCollapsedChange(true)} />
            <div className={styles.launcherComposer}>
              <ChatComposer
                conversationId={conversationId}
                onSend={handleSend}
                files={files}
                attachmentError={attachmentError}
                onFilesChange={setFiles}
                onAddFiles={addFiles}
                prefill={composerPrefill}
              />
            </div>
          </div>
        </div>
      </section>
    )
  }

  if (collapsed) {
    return (
      <button
        type="button"
        className={styles.openButton}
        aria-label={ready ? `Apri ${agentName}, risposta pronta` : `Apri ${agentName}`}
        title={ready
          ? `Apri ${agentName} · Risposta pronta`
          : `Apri ${agentName} · Trascina per spostare`}
        data-moved={launcherPulse > 0 || undefined}
        data-ready={ready || undefined}
        onPointerDown={launcherDrag.onPointerDown}
        onPointerMove={launcherDrag.onPointerMove}
        onPointerUp={launcherDrag.onPointerUp}
        onPointerCancel={launcherDrag.onPointerCancel}
        onClick={launcherDrag.onClick}
      >
        <LumaIcon animated={ready} />
        <span>{agentName}</span>
        {ready ? <span className={styles.readyDot} aria-hidden /> : null}
      </button>
    )
  }

  return (
    <section
      {...attachmentDropProps}
      className={`agent-chat agent-chat--ace ${styles.rail} ${workspace ? styles.workspaceSurface : ''} ${attachmentDropProps.className}`}
      aria-label={workspace ? `Chat con ${agentName}` : agentName}
      data-chat-collapsed="false"
      data-chat-workspace={workspace ? 'true' : 'false'}
    >
      <header className={styles.header}>
        {workspace ? (
          <button
            type="button"
            className={styles.headerButton}
            aria-label="Riduci a Luma"
            title="Riduci a Luma"
            onClick={onMinimizeWorkspace}
          >
            <Minimize2 size={18} aria-hidden />
          </button>
        ) : (
          <button
            type="button"
            className={styles.headerButton}
            aria-expanded="true"
            aria-label={`Chiudi ${agentName}`}
            title={`Chiudi ${agentName}`}
            onClick={() => onCollapsedChange(true)}
          >
            <PanelRightClose size={18} aria-hidden />
          </button>
        )}

        <div className={styles.headerIdentity}>
          <span>{workspace ? `Chat con ${agentName}` : agentName}</span>
        </div>
        <button
          type="button"
          className={`${styles.headerButton} ${styles.newChatButton}`}
          aria-label="Nuova conversazione"
          title="Nuova conversazione"
          onClick={onNewConversation}
        >
          <Plus size={17} aria-hidden />
        </button>
        {!workspace && onOpenWorkspace ? (
          <button
            type="button"
            className={styles.headerButton}
            aria-label="Continua a tutto schermo"
            title="Continua a tutto schermo"
            onClick={onOpenWorkspace}
          >
            <Maximize2 size={17} aria-hidden />
          </button>
        ) : null}
      </header>

      <div ref={conversationRef} className={styles.conversation} onScroll={handleScroll}>
        <div className={styles.messageList}>
          {messages.map((message) => {
            const messageActivities = terminalActivitiesByMessage.get(message.id) ?? []
            const activityCopy = activityTimelineCopy(
              promptsByTurnId.get(messageActivities[0]?.turnId ?? '') ?? '',
            )
            return (
              <div key={message.id} className={styles.turn} data-role={message.role}>
                {message.id === liveAssistantMessageId ? (
                  <div className={styles.liveActivitySlot}>{liveActivityControl}</div>
                ) : messageActivities.length > 0
                  && messageActivities[0].turnId
                  ? (
                  <button
                    type="button"
                    className={styles.completedActivityTrigger}
                    data-status={messageActivities.every((activity) => activity.status === 'error')
                      ? 'error'
                      : undefined}
                    aria-label={messageActivities.every((activity) => activity.status === 'error')
                      ? activityCopy.errorTrigger
                      : activityCopy.completedTriggerLabel}
                    aria-expanded={activityTurnId === messageActivities[0].turnId}
                    onClick={() => setActivityTurnId((current) =>
                      current === messageActivities[0].turnId ? null : messageActivities[0].turnId!)}
                  >
                    {messageActivities.every((activity) => activity.status === 'error')
                      ? activityCopy.errorTrigger
                      : activityCopy.completedTrigger}
                    <ChevronRight size={13} aria-hidden />
                  </button>
                ) : null}
                {message.role === 'assistant' ? (
                  <div
                    className={styles.assistantMessage}
                    data-interim={message.interim || undefined}
                  >
                    {message.interim ? (
                      <span className={styles.interimLabel}>in corso</span>
                    ) : null}
                    {assistantSegments(message.text, message.forms).map((segment, segmentIndex) =>
                      segment.kind === 'text' ? (
                        <ReactMarkdown
                          key={`text-${segmentIndex}`}
                          remarkPlugins={[remarkGfm]}
                          urlTransform={attachmentUrlTransform}
                          components={{ img: AssistantImage, a: (props) => <AssistantLink {...props} conversationId={conversationId} /> }}
                        >
                          {segment.text}
                        </ReactMarkdown>
                      ) : (
                        <InputRequestCard
                          key={segment.form.requestId}
                          form={segment.form}
                          answer={inputs[segment.form.requestId]}
                          readOnly={running}
                          onRespond={(outcome, value) =>
                            respondToInput(segment.form.requestId, outcome, value)}
                        />
                      ),
                    )}
                  </div>
                ) : (
                  <div className={styles.userMessage} data-pending={message.pending || undefined}>
                    {message.text ? <p>{message.text}</p> : null}
                    <AttachmentCards
                      items={message.attachments.map((attachment) => ({
                        key: attachment.id,
                        filename: attachment.filename,
                        contentType: attachment.contentType ?? 'application/octet-stream',
                        size: attachment.size ?? 0,
                        url: attachment.id.startsWith('pending-') ? undefined :
                          `/api/chat/${encodeURIComponent(conversationId)}/attachments/${attachment.id}`,
                      }))}
                    />
                  </div>
                )}
              </div>
            )
          })}

          {error ? <ChatErrorNotice error={error} onRetry={() => void retry()} /> : null}

          {running && liveAssistantMessageId === null ? (
            commentary ? (
              <div className={styles.turn} data-role="assistant">
                <div className={styles.liveActivitySlot}>{liveActivityControl}</div>
                <div className={styles.assistantMessage} data-interim>
                  <span className={styles.interimLabel}>in corso</span>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{ a: (props) => <AssistantLink {...props} conversationId={conversationId} /> }}
                  >
                    {commentary}
                  </ReactMarkdown>
                </div>
              </div>
            ) : (
              <div className={styles.nowLine}>{liveActivityControl}</div>
            )
          ) : null}
        </div>
      </div>

      {activityTurnId ? (
        <ActivityTimelineDrawer
          activities={selectedActivities}
          running={selectedActivityIsRunning}
          prompt={selectedActivityPrompt}
          onClose={() => setActivityTurnId(null)}
        />
      ) : null}

      <div className={styles.composerDock}>
        <ChatComposer
          conversationId={conversationId}
          onSend={handleSend}
          files={files}
          attachmentError={attachmentError}
          onFilesChange={setFiles}
          onAddFiles={addFiles}
        />
      </div>
    </section>
  )
}
